import { useEffect, useState } from "react";
import { NavLink, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { routePaths } from "../constants/routes";
import { addToCart } from "../services/cartService";
import { useCart } from "../hooks/useCart";
import { FALLBACK_PRODUCT_IMAGE, fetchProductById } from "../services/commerceApi";
import "../style/product_details.css";

export default function ProductDetails() {
  const navigate = useNavigate();
  const { id: productId } = useParams();
  const [product, setProduct] = useState(null);
  const [mainImage, setMainImage] = useState("");
  const { inCart, add } = useCart(productId);

  useEffect(() => {
    let isMounted = true;

    async function loadProduct() {
      if (!productId) {
        return;
      }

      try {
        const data = await fetchProductById(productId);

        if (!isMounted) {
          return;
        }

        setProduct(data);
        setMainImage(data.images[0] || data.url || FALLBACK_PRODUCT_IMAGE);
      } catch (error) {
        console.error("Product not found:", error);
      }
    }

    loadProduct();

    return () => {
      isMounted = false;
    };
  }, [productId]);

  if (!product) {
    return <div style={{ textAlign: "center", padding: "40px" }}>Loading product details...</div>;
  }

  const detailEntries = Object.entries(product.attributes || {}).filter(([, value]) => Boolean(value));

  const handleAddToCart = () => {
    if (inCart) {
      navigate(routePaths.cart);
      return;
    }

    add(product);
    toast.success(`${product.name} added to cart.`);
  };

  const handleBuyNow = () => {
    if (!inCart) {
      addToCart(product);
      toast.success(`${product.name} added to cart.`);
    }
    navigate(routePaths.cart);
  };

  return (
    <main className="product-page-container">
      <nav className="product-breadcrumb">
        <NavLink to="/home">Home</NavLink>
        <span>/</span>
        <NavLink to="/product">Catalog</NavLink>
        <span>/</span>
        <NavLink to={`/product_phone_tablets?category=${encodeURIComponent(product.category)}`}>
          {product.category}
        </NavLink>
        <span>/</span>
        <strong>{product.name}</strong>
      </nav>

      <section className="product-overview-banner">
        <div>
          <p className="product-category-label">{product.category}</p>
          <h1>{product.name}</h1>
          <p className="product-overview-copy">{product.description}</p>
        </div>
        <div className="product-overview-meta">
          <span>{product.brand}</span>
          <span>{product.reviewsLabel}</span>
          <span>{product.stock > 0 ? `${product.stock} in stock` : "Currently unavailable"}</span>
        </div>
      </section>

      <div className="product-content-wrapper">
        <div className="product-image-gallery">
          <img
            src={mainImage || FALLBACK_PRODUCT_IMAGE}
            className="main-image"
            alt={product.name}
            loading="eager"
            decoding="async"
          />

          <div className="product-thumbnail-gallery">
            {product.images.map((img, index) => (
              <img
                key={`${img}-${index}`}
                src={img}
                alt={`${product.name} ${index + 1}`}
                className={`thumbnail-item ${mainImage === img ? "active" : ""}`}
                onClick={() => setMainImage(img)}
                loading="lazy"
                decoding="async"
              />
            ))}
          </div>

          <NavLink
            to={`/product_phone_tablets?category=${encodeURIComponent(product.category)}`}
            className="buy-now-btn"
          >
            <i className="fas fa-arrow-right"></i> Explore More
          </NavLink>
          <button
            type="button"
            className="add-to-cart-btn"
            onClick={handleAddToCart}
            style={{ marginTop: "12px" }}
          >
            <i className="fas fa-cart-plus"></i> {inCart ? "Go to Cart" : "Add to Cart"}
          </button>
        </div>

        <div className="product-details-panel">
          <div className="price-stack">
            <p className="current-price">{product.currentPriceFormatted}</p>
            <p className="original-price">{product.originalPriceFormatted}</p>
            <p className="discount-percent">{product.discountPercent}</p>
          </div>
          <div className="rating-section">
            <div className="rating-badge">
              {product.rating} <i className="fas fa-star"></i>
            </div>
            <span className="reviews-count">
              {product.reviewCount.toLocaleString("en-IN")} ratings and reviews
            </span>
          </div>

          <section className="details-block">
            <h3>Description</h3>
            <p className="product-description">{product.description}</p>
          </section>

          <section className="details-block">
            <h3>Why this product stands out</h3>
            <div className="highlight-grid">
              {product.highlights.map((highlight) => (
                <div className="highlight-chip" key={highlight}>
                  <i className="fas fa-check-circle"></i>
                  <span>{highlight}</span>
                </div>
              ))}
            </div>
          </section>

          <section className="details-block">
            <h3>Key Specifications</h3>
            <ul id="pdp-specs">
              <li>
                <i className="fas fa-memory"></i> RAM: <span>{product.specs.ram}</span>
              </li>
              <li>
                <i className="fas fa-hdd"></i> Storage: <span>{product.specs.storage}</span>
              </li>
              <li>
                <i className="fas fa-battery-full"></i> Battery: <span>{product.specs.battery}</span>
              </li>
              <li>
                <i className="fas fa-palette"></i> Color: <span>{product.color}</span>
              </li>
            </ul>
          </section>

          <section className="details-block">
            <h3>{product.category} Details</h3>
            <div className="attribute-grid">
              {detailEntries.map(([key, value]) => (
                <div className="attribute-row" key={key}>
                  <span>{key.replace(/([A-Z])/g, " $1").replace(/^./, (char) => char.toUpperCase())}</span>
                  <strong>{value}</strong>
                </div>
              ))}
            </div>
          </section>

          <section className="details-block details-meta-grid">
            <div>
              <h3>Seller</h3>
              <p className="product-description">{product.seller}</p>
            </div>
            <div>
              <h3>Delivery</h3>
              <p className="product-description">{product.delivery}</p>
            </div>
            <div>
              <h3>Return Policy</h3>
              <p className="product-description">{product.returnPolicy}</p>
            </div>
            <div>
              <h3>Warranty</h3>
              <p className="product-description">{product.warranty}</p>
            </div>
          </section>

          <section className="details-block">
            <h3>Purchase Actions</h3>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "12px" }}>
              <button
                type="button"
                onClick={handleAddToCart}
                style={{
                  minHeight: "46px",
                  padding: "12px 18px",
                  borderRadius: "12px",
                  border: "none",
                  background: "#1d4ed8",
                  color: "#fff",
                  fontWeight: 700,
                  cursor: "pointer",
                }}
              >
                {inCart ? "Go to Cart" : "Add to Cart"}
              </button>
              <button
                type="button"
                onClick={handleBuyNow}
                style={{
                  minHeight: "46px",
                  padding: "12px 18px",
                  borderRadius: "12px",
                  border: "none",
                  background: "#ea580c",
                  color: "#fff",
                  fontWeight: 700,
                  cursor: "pointer",
                }}
              >
                Buy Now
              </button>
            </div>
          </section>
        </div>
      </div>
    </main>
  );
}
